import {Component, OnDestroy, OnInit} from '@angular/core';
import {combineLatest, Observable, of} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {FormulaireService} from '../../../common/services/formulaire/formulaire.service';
import {BlockForm, FieldConfigName, FormModel, FormState, FormType} from '../../../common/components/form/models';
import {IndividuService, PubSubService} from '@Common/core/services';
import {SubSink} from '@Common/core/utils/subsink';
import {catchError, finalize, switchMap} from 'rxjs/operators';
import {SnackBarToastyService, typeToast} from '@Common/widgets/general/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {ModaleRenduFormulaireComponent} from '../rendu-formulaire/modale-rendu-formulaire-container/modale-rendu-formulaire.component';
import {UrlService} from '../../../common/services/url/url.service';
import {UrlModel} from '../../../common/services/url/url.model';
import {HelperService} from '../../../common/services/helper.service';

@Component({
  selector: 'app-edit-formulaire',
  templateUrl: './edit-formulaire.component.html',
  styleUrls: ['./edit-formulaire.component.scss']
})
export class EditFormulaireComponent implements OnInit, OnDestroy {
  subSink = new SubSink();
  formEdit: FormModel;
  formToSubmit: FormModel;
  onSaving = false;
  screenHeight: number;
  screenWidth: number;
  constructor(private route: ActivatedRoute,
              private router: Router,
              public dialog: MatDialog,
              private pubSubSvc: PubSubService,
              private snackSvc: SnackBarToastyService,
              private helperSvc: HelperService,
              private formulaireSvc: FormulaireService,
              private individuService: IndividuService,
              private urlSvc: UrlService) {
  }

  ngOnInit() {
    this.getFormById();
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  getFormById() {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.subSink.sink = this.formulaireSvc
        .get(id)
        .subscribe(_form => this.formEdit = _form);
    } else {
      this.formEdit =  {
        id: 0,
        type: FormType.Demande,
        date_debut: null,
        date_fin: null,
        nom: 'Titre du formulaire...',
        description: 'Description du formulaire...',
        private: false,
        state: FormState.draft,
        blocks: []
      };
    }
  }

  getForm(formModel: FormModel) {
    this.formToSubmit = formModel;
  }

  checkIfFormContainsFields() {
    const formFielsCount = this.formToSubmit.blocks.reduce((count, blk) => count + blk.fields.length, 0);
    return !!formFielsCount;
  }

  /**
   * Constructions des fields pour sauvegarde
   * @param {BlockForm} blk
   */
  mapBlock(blk): BlockForm {
    return <BlockForm> {
      nom: blk.nom,
      description: blk.description,
      position: blk.position,
      type: blk.type,
      fields: this.mapBlockFields(blk, blk.fields),
      condition_obj: blk.condition_obj || {},  // champ obligatoire pour la validation par serializer django
    };
  }

  mapBlockFields(blk, fields): FieldConfigName[] {
    return fields.map((f, k) => this.mapkField(blk, f, k));
  }

  mapkField(blk, f, k): FieldConfigName {
    return <FieldConfigName> {
      id: f.id,
      defaultFieldId: f.id,
      options: f.options,
      position: k + 1
    };
  }

  /**
   * Génére un slug pour ce formulaire
   * @param {FormModel} form
   * @private
   */
  private _generateUrl(form: FormModel) {
    const urlForm = <UrlModel> {
      id: 0,
      form: form.id,
      description: form.description
    };
    this.urlSvc.updateOrcreate(urlForm).subscribe(_url => console.log(_url));
  }

  /**
   * Met à jour le slug suite à la modification du formulaire pour correspondre au nom
   * @param {FormModel} form
   * @private
   */
  private _updateUrl(form: FormModel) {
    this.urlSvc.slugUpdate(form).subscribe(_url => {
      if (_url && _url['modified']) {
        this.formEdit.url_obj[0].slug = _url['slug'];
      }
    });
  }
  /**
   * Création formulaire, 1ère fois
   * l'API pour la conception est dédiée à cela, en raison des objets embarqués (nested)
   * - création en base du formulaire
   * - création de l'url lié à ce formulaire
   * - redirection vers l'édition / modification pour garder la fenêtre
   */
  create(): Observable<any> {
    return new Observable(observer => {
      this.pubSubSvc.publish('loading', true);
      const observable = combineLatest([
        this.formulaireSvc.createConceptionForm(this.formToSubmit),
        this.individuService.individu$,
      ]);
      this.subSink.sink = observable
        .pipe(
          switchMap( ([_formModel, _ind]) => this.formulaireSvc.addFormGestionnaire(_formModel, _ind.uid)),
          finalize(() => {
            this.pubSubSvc.publish('loading', false);
            this.onSaving = false;
          }),
           catchError(_ => of(null)))
        .subscribe((result: FormModel) => {
          if (result) {
            this._generateUrl(result);
            this.formEdit = result;
            this.onSaving = false;
            this.snackSvc.toasty('Formulaire', 'Le formulaire a bien été enregistré', typeToast.success, 1500);
            observer.next(true);
            this.router.navigate(['edit', this.formEdit.uuid], {relativeTo: this.route.parent});
          } else {
            this.snackSvc.toasty('Formulaire', 'Une erreur est intervenue lors de l\'enregistrement, veuillez recommencer ou prévenir le support', typeToast.error);
            this.router.navigate(['/gestion']);
            observer.next(false);
          }
          observer.complete();
        });
    });
  }

  /**
   * Modification formulaire existant en observable
   * l'API pour la conception est dédiée à cela, en raison des objets embarqués (nested)
   */
  update(): Observable<any> {
    return new Observable(observer => {
      this.pubSubSvc.publish('loading', true);
      this.subSink.sink = this.formulaireSvc
        .updateConceptionForm(this.formToSubmit)
        .pipe(finalize(() => {
            this.pubSubSvc.publish('loading', false);
            this.onSaving = false;
        }),
             catchError(_ => of(null)))
        .subscribe((result: FormModel) => {
          this.onSaving = false;
          if (result) {
            this._updateUrl(result);
            this.snackSvc.toasty('Formulaire', 'Le formulaire a bien été enregistré', typeToast.success, 1500);
            observer.next(true);
          } else {
            this.snackSvc.toasty('Formulaire', 'Une erreur est intervenue lors de l\'enregistrement', typeToast.error);
            observer.next(false);
          }
          observer.complete();
        });
      });
  }
  /**
   * Enregistrement du formulaire
   * le gestionnaire qui l'a édité devient gestionnaire
   */
  onSubmitForm(preview = false) {
    this.onSaving = true;
    this.formToSubmit.blocks = this.formToSubmit.blocks
      .filter(blk => blk.fields.length > 0)
      .map(blk => this.mapBlock(blk));
    let createOrupdate;
    if (this.formToSubmit.id === 0) {
      createOrupdate = this.create();
    } else {
      createOrupdate = this.update();
    }
    createOrupdate.subscribe(result => {
      if (result && preview) {
        this._openPreview();
      }
    });
  }
  previewForm(go = false) {
    if (go) {
      this.onSubmitForm(go);
    }
  }
  /**
   * Ouverture modale de prévisualisation - sans cadre gestion - s'adapte à l'écran
   * @private
   */
  private _openPreview() {
    [this.screenHeight, this.screenWidth] = this.helperSvc.getScreenSize();
    const top = 50;
    const width = this.screenWidth - 50;
    const height = this.screenHeight - (2 * top);
    const dialogRef = this.dialog.open(ModaleRenduFormulaireComponent, {
      width: '680px',
      data: this.formEdit,
    });
    dialogRef.updatePosition({top: `${top}px`});
    dialogRef.updateSize(`${width}px`, `${height}px`);
  }
}
